/**
 * Specific Pages Control Component
 * 
 * @package TopBuddy
 */

import { useState, useEffect, useMemo, useCallback } from '@wordpress/element';
import { FormTokenField, BaseControl, Spinner } from '@wordpress/components';
import { __ } from '@wordpress/i18n';

// Debounce helper function.
const debounce = (func, wait) => {
	let timeout;
	return function executedFunction(...args) {
		const later = () => {
			clearTimeout(timeout);
			func(...args);
		};
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
};

const SpecificPagesControl = ({ value, onChange, nonce, ajaxUrl, initialLabels }) => {
	const [suggestions, setSuggestions] = useState([]);
	const [isSearching, setIsSearching] = useState(false);
	const [labels, setLabels] = useState(initialLabels || {});

	// Create a map of IDs to labels for selected items.
	useEffect(() => {
		// Merge initial labels with current labels.
		setLabels(prevLabels => ({
			...prevLabels,
			...initialLabels,
		}));
	}, [initialLabels]);

	// Search function.
	const performSearch = useCallback(async (searchTerm) => {
		if (!searchTerm || searchTerm.length < 2) {
			setSuggestions([]);
			return;
		}

		setIsSearching(true);

		try {
			const formData = new FormData();
			formData.append('action', 'topbuddy_search_specific_pages');
			formData.append('nonce', nonce);
			formData.append('search', searchTerm);

			const response = await fetch(ajaxUrl, {
				method: 'POST',
				body: formData,
			});

			const data = await response.json();

			if (data.success && data.data && data.data.results) {
				// Transform grouped results to flat array of suggestions.
				const flatSuggestions = [];
				const newLabels = {};

				data.data.results.forEach(group => {
					group.children.forEach(item => {
						flatSuggestions.push(item.text);
						newLabels[item.id] = item.text;
					});
				});

				setSuggestions(flatSuggestions);
				
				// Update labels map.
				setLabels(prevLabels => ({
					...prevLabels,
					...newLabels,
				}));
			} else {
				setSuggestions([]);
			}
		} catch (error) {
			console.error('Search failed:', error);
			setSuggestions([]);
		} finally {
			setIsSearching(false);
		}
	}, [nonce, ajaxUrl]);

	// Debounced search.
	const debouncedSearch = useMemo(
		() => debounce(performSearch, 300),
		[performSearch]
	);

	// Handle input change for search.
	const handleInputChange = (searchTerm) => {
		debouncedSearch(searchTerm);
	};

	// Get display values (labels) for selected IDs.
	const displayValues = value.map(id => labels[id] || id);

	// Handle token change.
	const handleChange = (tokens) => {
		// Convert labels back to IDs.
		const ids = tokens.map(token => {
			// Find the ID for this label.
			const entry = Object.entries(labels).find(([id, label]) => label === token);
			return entry ? entry[0] : null;
		}).filter(Boolean);

		onChange(ids);
	};

	return (
		<div className="topbuddy-display-rules__field">
			<BaseControl
				label={__('Search Specific Pages', 'topbuddy')}
				help={__('Search and select specific posts, pages, or taxonomy terms', 'topbuddy')}
				id="topbuddy-specific-pages"
			>
			<FormTokenField
				value={displayValues}
				suggestions={suggestions}
				onChange={handleChange}
				onInputChange={handleInputChange}
				placeholder={__('Search pages, posts, or terms...', 'topbuddy')}
				maxSuggestions={20}
				__experimentalShowHowTo={false}
				__experimentalExpandOnFocus={true}
			/>
				{isSearching && (
					<div className="topbuddy-display-rules__specific-pages-loading">
						<Spinner />
						<span>{__('Searching...', 'topbuddy')}</span>
					</div>
				)}
			</BaseControl>
		</div>
	);
};

export default SpecificPagesControl;
